import { IContextualMenuItem } from 'office-ui-fabric-react/lib/components/ContextualMenu';
import createSubMenuItem from './createSubMenuItem';
import RetentionPolicyTag from 'owa-service/lib/contract/RetentionPolicyTag';

export interface HierarchyRetentionPolicyTag {
    parentPolicyTag: RetentionPolicyTag;
    childrenPolicyTags: RetentionPolicyTag[];
}

/**
 * Utility to create a sub menu item for the delete policies. In case of delete policies, hierarchical relationship is possible
 * @param hierarchyTag the custom hierarchy tag created to store parent child relationships
 * @param showTagSelection whether to show a selected sub menu item as checked
 * @param currentDeleteTagId currently selected delete policy tag's value
 * @param onDeletePolicyClickCallback callback when a delete policy is selected
 * @returns an object where item is the hierarchical contextual menu item and policyFound denotes whether the selected policy is found among these items
 */
export default function createHierarchialSubMenuDeleteItem(
    hierarchyTag: HierarchyRetentionPolicyTag,
    showTagSelection: boolean,
    currentDeleteTagId: string,
    onDeletePolicyClickCallback: (
        ev: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item: IContextualMenuItem
    ) => void
): { item: IContextualMenuItem; policyFound: boolean } {
    const { parentPolicyTag, childrenPolicyTags } = hierarchyTag;
    const isRootSelected = parentPolicyTag.RetentionId === currentDeleteTagId;
    // If childern tags do not exist, create menu item with the parent itself
    if (childrenPolicyTags == null || childrenPolicyTags.length == 0) {
        return {
            item: createSubMenuItem({
                retentionId: parentPolicyTag.RetentionId,
                displayName: parentPolicyTag.DisplayName,
                isChecked: showTagSelection && isRootSelected,
                subMenuProps: null,
                onClickCallback: onDeletePolicyClickCallback,
            }),
            policyFound: isRootSelected,
        };
    }
    let selectedPolicyFound = false;
    // create the list of child menu items for a parent tag
    const optionMenuItems = childrenPolicyTags.map((childTag: RetentionPolicyTag) => {
        const isChildSelected = childTag.RetentionId === currentDeleteTagId;
        if (isChildSelected) {
            selectedPolicyFound = true;
        }
        return createSubMenuItem({
            retentionId: childTag.RetentionId,
            displayName: childTag.DisplayName,
            isChecked: showTagSelection && isChildSelected,
            subMenuProps: null,
            onClickCallback: onDeletePolicyClickCallback,
        });
    });

    // create a parent menu and add the sub-menu
    return {
        item: createSubMenuItem({
            retentionId: parentPolicyTag.RetentionId,
            displayName: parentPolicyTag.DisplayName,
            isChecked: showTagSelection && isRootSelected,
            subMenuProps: { items: optionMenuItems } /* children added as subMenuProps */,
            onClickCallback: null,
        }),
        policyFound: selectedPolicyFound,
    };
}
